/**
 * Step 07 — how big the captions are (spec §2.2, `captionSize`).
 *
 * Three cards, not a slider: a first-time user is not being asked for a
 * number, they are being asked whether they want the phone-first look or the
 * one that fits more words per line. The exact px lives in Adjust.
 *
 * The screen's own idea is the FIT LINE under the cards. "Large" means
 * nothing until you know it turns one sentence into four lines, so the step
 * measures the sample's longest cue in the operator's OWN caption face with a
 * canvas and says so in words. The measurement is deliberately the same
 * arithmetic the render does — canvas px on a 2160-wide frame, the caption's
 * own side padding subtracted — so the sentence is a fact, not a guess.
 *
 * NOTHING is dispatched on mount: Medium is the language's own default, so an
 * untouched walk-through writes no key at all (spec §2.3, pinned by
 * `payload.test.ts`).
 */
import { useEffect, useMemo, useState, type Dispatch } from "react";
import {
  SIZE_PRESETS,
  logoPatchForCaption,
  nearestSizePreset,
  sizePatchFor,
  type SizeName,
} from "../../../lib/brandSetup/geometry";
import { LAYOUT_DEFAULTS, buildBaseLayout } from "../../../lib/brandSetup/payload";
import { effectiveDesign, effectiveFontNames, type BrandSetupAction, type BrandSetupState } from "../../../lib/brandSetup/state";
import { sampleLongestCue } from "../../../lib/onboardingSample";
import { ReelTag } from "../ui";

/** The chip the study writes when it read a caption size off a reel. */
const SIZE_FIELD = "layout.caption.fontSizePx";

const CARDS: ReadonlyArray<{ id: SizeName; label: string; hint: string }> = [
  { id: "small", label: "Small", hint: "More words per line" },
  { id: "medium", label: "Medium", hint: "Our default" },
  { id: "large", label: "Large", hint: "Easiest to read on a phone" },
];

/** The canvas the whole step measures on. One per document — a fresh canvas
 *  per keystroke is a new GPU surface per keystroke. */
let sharedCtx: CanvasRenderingContext2D | null | undefined;
function measureCtx(): CanvasRenderingContext2D | null {
  if (sharedCtx === undefined) {
    try {
      sharedCtx = document.createElement("canvas").getContext("2d");
    } catch {
      sharedCtx = null;
    }
  }
  return sharedCtx;
}

const SYSTEM_SANS = "system-ui, -apple-system, 'Segoe UI', sans-serif";

/**
 * Is this face actually available to measure with?
 *
 * `measureText` in a family the document has not loaded silently falls back
 * to the system sans and returns ITS widths — which would put a confident
 * "wraps to 2 lines" under a face that wraps to 3. So the face is checked,
 * and when it is not there yet the measurement says system sans and means it.
 */
function faceReady(family: string): boolean {
  try {
    return typeof document !== "undefined" && !!document.fonts && document.fonts.check(`700 100px "${family}"`);
  } catch {
    return false;
  }
}

function useFaceReady(family: string): boolean {
  const [ready, setReady] = useState(() => faceReady(family));
  useEffect(() => {
    let live = true;
    setReady(faceReady(family));
    if (typeof document === "undefined" || !document.fonts) return;
    // The webfont arrives a beat after the step does; re-measure when it lands.
    document.fonts
      .load(`700 100px "${family}"`)
      .then(() => { if (live) setReady(faceReady(family)); })
      .catch(() => { /* not a library face — the fallback measurement stands */ });
    return () => { live = false; };
  }, [family]);
  return ready;
}

export function CaptionSizeStep(props: {
  state: BrandSetupState;
  dispatch: Dispatch<BrandSetupAction>;
}): React.JSX.Element {
  const { state, dispatch } = props;
  const direction = state.language.direction === "rtl" ? "rtl" : "ltr";
  const presets = SIZE_PRESETS[direction];

  const base = useMemo(() => buildBaseLayout(state), [state]);
  const baseSize = base.caption?.fontSizePx ?? presets.medium;
  const basePadding = base.caption?.sidePaddingPx ?? 140;
  // Base ⊕ everything decided so far (steps AND the operator's fine-tuning).
  const design = effectiveDesign(state)?.layout?.caption;
  const effectivePx = design?.fontSizePx ?? baseSize;
  const current = nearestSizePreset(direction, effectivePx);
  // A bigger caption in the lower third grows UP into the logo, so the size
  // card carries the logo with it — but only while Lower third is the answer.
  const lowered = (design?.justify ?? base.caption?.justify) === "flex-end";
  const bottomPx = design?.bottomPx ?? base.caption?.bottomPx ?? 0;
  const logoBottomPx =
    effectiveDesign(state)?.layout?.logo?.bottomPx ?? base.logo?.bottomPx ?? LAYOUT_DEFAULTS.logoBottomPx;

  const bodyFace = effectiveFontNames(state).body;
  const ready = useFaceReady(bodyFace);
  const longest = sampleLongestCue(direction);

  const lines = useMemo(() => {
    const ctx = measureCtx();
    if (!ctx) return null;
    ctx.font = `700 ${presets[current]}px ${ready ? `"${bodyFace}", ${SYSTEM_SANS}` : SYSTEM_SANS}`;
    const width = ctx.measureText(longest).width;
    const usable = 2160 - 2 * basePadding;
    if (!(width > 0) || usable <= 0) return null;
    return Math.max(1, Math.ceil(width / usable));
  }, [presets, current, ready, bodyFace, longest, basePadding]);

  const chip = state.chips.find((c) => c.field === SIZE_FIELD);
  const fromReel = chip && state.accepted[chip.field] !== false;

  return (
    <div style={{ display: "grid", gap: 16 }}>
      {fromReel && (
        <ReelTag
          label={chip.source === "adjusted" ? "From your reel, sized to fit" : "From your reel"}
          onReset={() => dispatch({ type: "resetChip", field: SIZE_FIELD })}
        />
      )}

      <div role="radiogroup" aria-label="Caption size" style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10 }}>
        {CARDS.map((card, i) => {
          const on = card.id === current;
          return (
            <button
              key={card.id}
              type="button"
              role="radio"
              aria-checked={on}
              {...(i === 0 ? { "data-autofocus": true } : {})}
              onClick={() => {
                // A smaller caption in the lower third gives the room back, so
                // the logo returns to the founding line rather than staying
                // wherever a bigger size once pushed it.
                const logo = logoPatchForCaption({
                  lowered,
                  fontSizePx: presets[card.id],
                  captionBottomPx: bottomPx,
                  currentLogoBottomPx: logoBottomPx,
                });
                dispatch({
                  type: "layoutPatch",
                  patch: { caption: sizePatchFor(direction, card.id), ...(logo ? { logo } : {}) },
                  label: `Caption size → ${card.label}`,
                });
              }}
              style={{
                display: "grid",
                gap: 6,
                justifyItems: "center",
                padding: "16px 10px 14px",
                borderRadius: 16,
                cursor: "pointer",
                fontFamily: "inherit",
                border: on ? "1.5px solid var(--ql-yellow)" : "1px solid rgba(255,255,255,.10)",
                background: on ? "rgba(255,213,0,.07)" : "rgba(255,255,255,.02)",
                transition: "all .15s var(--ql-ease)",
              }}
            >
              {/* A specimen, sized in PROPORTION to the three presets, so the
                  cards differ by the thing they change. */}
              <span
                aria-hidden
                style={{
                  fontFamily: `"${bodyFace}", ${SYSTEM_SANS}`,
                  fontWeight: 700,
                  lineHeight: 1,
                  height: 46,
                  display: "flex",
                  alignItems: "center",
                  fontSize: Math.round((presets[card.id] / presets.large) * 40),
                  color: on ? "var(--ql-yellow)" : "var(--ql-fg-muted)",
                }}
              >
                {direction === "rtl" ? "أبج" : "Aa"}
              </span>
              <span style={{ fontSize: 13.5, fontWeight: 700, color: "var(--ql-fg)" }}>{card.label}</span>
              <span style={{ fontSize: 11.5, lineHeight: 1.4, color: "var(--ql-fg-subtle)", textAlign: "center" }}>{card.hint}</span>
            </button>
          );
        })}
      </div>

      {lines !== null && (
        <div aria-live="polite" style={{ fontSize: 13.5, color: "var(--ql-fg-muted)", lineHeight: 1.6 }}>
          Long sentences wrap to {lines} {lines === 1 ? "line" : "lines"} at this size.
        </div>
      )}
    </div>
  );
}
